import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/services/queryClient'
import { createPattern, deletePattern, fetchPatterns, updatePattern } from './api'

/**
 * Shift patterns.
 *
 * One query for the whole set and no detail read: every pattern is fully
 * described by the list — `ShiftOut` is what `GET /shifts/{id}` returns too —
 * so opening the drawer needs no second request, and the overlap warning needs
 * the whole set anyway. See `fetchPatterns`.
 */
export function useShiftPatterns({ enabled = true } = {}) {
  return useQuery({
    queryKey: queryKeys.shiftPatterns,
    queryFn: ({ signal }) => fetchPatterns(signal),
    /* Off for a reader without `shifts.manage` — Shift Handover asks only when
       the catalogue is readable, rather than drawing a 403 as an empty list. */
    enabled,
    staleTime: 30_000,
  })
}

/**
 * Create, update and delete.
 *
 * None retried. Every way these fail is an answer — a plant the account does not
 * own, a day list that is empty, a pattern already gone — and asking again gets
 * the same one. A create that succeeded and merely failed to report would leave
 * two identical patterns claiming the same window, which is precisely the state
 * this screen exists to warn about.
 */
export function useCreatePattern() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: createPattern,
    retry: false,
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.shiftPatterns }),
  })
}

export function useUpdatePattern() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: updatePattern,
    retry: false,
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.shiftPatterns }),
  })
}

export function useDeletePattern() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: deletePattern,
    retry: false,
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.shiftPatterns }),
  })
}
